/** Hooks the three apps share. */

import { useCallback, useEffect, useRef, useState } from "react";
import { startAnalytics, trackPage } from "./analytics";

export interface Async<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Fetch on mount, and expose all four states separately.
 *
 * `data === null && !loading && !error` is a real state and a different sentence from the other
 * three — it is the one a view renders as "nothing here yet", and collapsing it into `loading`
 * is how a broken panel becomes indistinguishable from an empty one.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Guards a setState after unmount, and — more usefully — a slow first response landing on
  // top of a fast second one when the deps change mid-flight.
  const seq = useRef(0);

  const run = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fn();
      if (seq.current === mine) setData(result);
    } catch (e) {
      if (seq.current === mine) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (seq.current === mine) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void run();
  }, [run]);

  return { data, loading, error, reload: run };
}

/** Respect the OS setting. Every animation in this design is decoration over a page that reads
 *  fine without it, so there is no case where ignoring this is the right call. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

/**
 * Light or dark, with an explicit choice beating the system in BOTH directions.
 *
 * That is why `tokens.css` states light and dark overrides rather than relying on the media
 * query alone: someone who chose light on a dark-mode machine must get light, and a rule that
 * only overrides one way silently ignores half of them.
 */
export function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState<string>(
    () => localStorage.getItem("authoxi-theme") ?? "",
  );

  useEffect(() => {
    if (theme) document.documentElement.dataset.theme = theme;
  }, [theme]);

  const toggle = useCallback(() => {
    const now =
      document.documentElement.dataset.theme ||
      (matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    const next = now === "dark" ? "light" : "dark";
    localStorage.setItem("authoxi-theme", next);
    setTheme(next);
  }, []);

  return [theme, toggle];
}

/** Reveal-on-scroll. A no-op when reduced motion is on, rather than a faster animation. */
export function useReveal(enabled = true) {
  useEffect(() => {
    if (!enabled || typeof IntersectionObserver !== "function") return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.12 },
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
}

/**
 * Start analytics, and record a view every time the route changes.
 *
 * TAKES THE RESOLVED PATH, not `location.pathname`, and that is the whole reason this is a hook
 * rather than a script tag. An SPA's automatic pageview fires once, on load, before the router
 * has decided anything — so every view in the session is attributed to the URL the visitor
 * entered on, and a report of "which page do people read" answers "the one they landed on",
 * every time, which looks plausible and is useless.
 *
 * `cookieless` defaults to true. Pass `false` only on a surface behind a key.
 */
export function usePageViews(path: string, { cookieless = true }: { cookieless?: boolean } = {}) {
  useEffect(() => {
    startAnalytics({ cookieless });
  }, [cookieless]);

  useEffect(() => {
    trackPage(path);
  }, [path]);
}
